import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowDown } from "lucide-react";
import { api } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { money } from "../lib/data";
import SignupSection from "../components/SignupSection";

const EASE = [0.76, 0, 0.24, 1];

function Hero() {
  const reduce = useReducedMotion();
  return (
    <section data-testid="hero-section"
             className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-ll-black">
      <h1 data-testid="hero-title" aria-label="ETERNEL"
          className="select-none text-center font-display text-[17.5vw] font-bold leading-[0.9] tracking-[-0.03em] text-ll-bone md:text-[15vw]">
        {"ETERNEL".split("").map((ch, i) => (
          <motion.span key={i} className="inline-block" aria-hidden="true"
                       initial={reduce ? false : { opacity: 0, y: 60 }}
                       animate={{ opacity: 1, y: 0 }}
                       transition={{ duration: 0.7, delay: 0.2 + i * 0.055, ease: EASE }}>
            {ch}
          </motion.span>
        ))}
      </h1>
      <motion.p
        data-testid="hero-tagline"
        className="mt-8 font-tech text-[11px] tracking-[0.45em] text-ll-steel"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.9 }}
      >
        PREMIUM LUXURY ROOM DECOR
      </motion.p>
      <motion.a
        href="#shelf"
        data-testid="hero-scroll-cue"
        aria-label="Scroll to products"
        className="absolute bottom-10 flex flex-col items-center gap-3 font-tech text-[10px] tracking-[0.35em] text-ll-steel transition-colors duration-200 hover:text-ll-bone"
        initial={reduce ? false : { opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 1.3 }}
      >
        SCROLL
        <motion.span
          animate={reduce ? {} : { y: [0, 8, 0] }}
          transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
        >
          <ArrowDown className="h-4 w-4" />
        </motion.span>
      </motion.a>
    </section>
  );
}

function HomeCard({ product, index }) {
  const { addToCart } = useShop();
  const reduce = useReducedMotion();
  const frame = product.wide ? "aspect-[4/3]" : "aspect-[4/5]";
  return (
    <motion.article
      data-testid={`home-card-${product.slug}`}
      className="group"
      initial={reduce ? false : { opacity: 0, y: 36 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-70px" }}
      transition={{ duration: 0.55, delay: (index % 3) * 0.08, ease: "easeOut" }}
    >
      <Link to={`/product/${product.slug}`} data-testid={`home-card-link-${product.slug}`}
            className={`relative block ${frame} overflow-hidden bg-ll-coal`}>
        <img src={product.images[0]} alt={product.name} loading="lazy"
             className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.04]" />
        {product.images[1] && (
          <img src={product.images[1]} alt="" loading="lazy" aria-hidden="true"
               className="absolute inset-0 h-full w-full object-cover opacity-0 transition-opacity duration-500 group-hover:opacity-100" />
        )}
        <button
          data-testid={`home-add-${product.slug}`}
          onClick={(e) => { e.preventDefault(); addToCart(product); }}
          className="absolute inset-x-0 bottom-0 translate-y-full bg-ll-bone py-3.5 text-center font-tech text-[11px] tracking-[0.3em] text-ll-black transition-transform duration-300 ease-out group-hover:translate-y-0"
        >
          ADD TO CART
        </button>
      </Link>
      <div className="mt-3 flex items-baseline justify-between gap-3">
        <Link to={`/product/${product.slug}`}
              className="font-display text-sm font-bold tracking-wide transition-colors duration-200 hover:text-ll-steel">
          {product.name}
        </Link>
        <p className="font-tech text-xs text-ll-steel">{money(product.price)}</p>
      </div>
    </motion.article>
  );
}

function Products({ products }) {
  return (
    <section id="shelf" data-testid="shelf-section" className="mx-auto max-w-7xl px-5 py-20 md:px-8 md:py-28">
      <motion.div
        className="flex items-end justify-between border-b border-ll-line pb-5"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
      >
        <h2 data-testid="shelf-heading" className="font-display text-xl font-bold tracking-[0.08em]">LATEST PIECES</h2>
        <Link to="/shop" data-testid="view-all-link"
              className="font-tech text-[11px] tracking-[0.25em] text-ll-steel transition-colors duration-200 hover:text-ll-bone">
          VIEW ALL — {products.length}
        </Link>
      </motion.div>
      <div className="mt-12 grid grid-cols-2 gap-x-4 gap-y-12 md:grid-cols-3 md:gap-x-6">
        {products.map((p, i) => (
          <HomeCard key={p.slug} product={p} index={i} />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [products, setProducts] = useState([]);
  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-ll-black">
      <Hero />
      {products.length > 0 && <Products products={products} />}
      <SignupSection />
    </div>
  );
}
